import React, { useMemo, useState } from "react";
import {
  Box,
  Button,
  Card,
  CardContent,
  Container,
  Divider,
  Grid,
  Stack,
  TextField,
  Typography,
  darken,
  useMediaQuery,
  useTheme,
} from "@mui/material";
import LocalPhoneIcon from "@mui/icons-material/LocalPhone";
import MailOutlineIcon from "@mui/icons-material/MailOutline";
import PlaceIcon from "@mui/icons-material/Place";
import { Link } from "react-router-dom";
import emailjs from "emailjs-com";
import Swal from "sweetalert2";
import { appTheme } from "../../../shared/theme";
import { CustomRoundButton } from "Layout/CustomButton";

const SERVICE_ID = "service_iuq2c7c";
const TEMPLATE_ID = "template_8khflwo";
const USER_ID = "BYnD6lRrcDdjsaoXt";

const isValidEmail = (value) => {
  const v = String(value || "").trim();
  if (!v) return false;
  return /^[^\s@]+@[^\s@]+\.[^\s@]{2,}$/i.test(v);
};

export const ContactContent = () => {
  const theme = useTheme();
  const isSmDown = useMediaQuery(theme.breakpoints.down("sm"));

  const [values, setValues] = useState({
    firstName: "",
    lastName: "",
    email: "",
    comment: "",
  });

  const [touched, setTouched] = useState({
    email: false,
  });

  const emailInvalid = touched.email && values.email.trim() !== "" && !isValidEmail(values.email);

  const validationCheck = useMemo(() => {
    const anyEmpty = Object.values(values).some((v) => !String(v || "").trim());
    const emailBad = !isValidEmail(values.email);
    return anyEmpty || emailBad;
  }, [values]);

  const handleSubmit = (e) => {
    e.preventDefault();

    if (!isValidEmail(values.email)) {
      setTouched((t) => ({ ...t, email: true }));
      Swal.fire({
        icon: "error",
        title: "Check your email",
        text: "Please enter a valid email address so we can reply.",
        confirmButtonColor: appTheme.palette.yellow.main,
      });
      return;
    }

    emailjs.sendForm(SERVICE_ID, TEMPLATE_ID, e.target, USER_ID).then(() => {
      Swal.fire({
        icon: "success",
        title: "Success",
        text: "Your message has been sent, we will respond shortly.",
        confirmButtonColor: appTheme.palette.green.main,
      });
    });

    e.target.reset();
    setValues({ firstName: "", lastName: "", email: "", comment: "" });
    setTouched({ email: false });
  };

  const openMaps = () => {
    window.open("https://goo.gl/maps/168ZM835dShXM1An6", "_blank");
  };

  const headingFont =
    appTheme.typography?.h1?.fontFamily ||
    appTheme.typography?.body1?.fontFamily;
  const bodyFont = appTheme.typography?.body1?.fontFamily;

  const glassCardSx = {
    height: "100%",
    borderRadius: 3,
    border: "1px solid rgba(255,255,255,0.14)",
    background: "rgba(255,255,255,0.07)",
    backdropFilter: "blur(10px)",
  };

 const fieldSx = {
  "& .MuiOutlinedInput-root": {
    backgroundColor: appTheme.palette.white.main,
    borderRadius: 2,
    "& fieldset": {
      borderColor: "rgba(0,0,0,0.12)",
    },
    "&:hover fieldset": {
      borderColor: "rgba(0,0,0,0.28)",
    },
    "&.Mui-focused fieldset": {
      borderColor: appTheme.palette.yellow.main,
      borderWidth: 2,
    },
  },
  "& .MuiInputBase-input": {
    color: "rgba(0,0,0,0.87)",
    caretColor: appTheme.palette.yellow.main,
  },
  "& .MuiInputAdornment-root": {
    color: "rgba(0,0,0,0.45)",
  },
  "& .MuiFormHelperText-root.Mui-error": {
    fontWeight: 900,
  },
};

  const ContactTile = ({
    icon,
    label,
    title,
    lines,
    onClick = undefined,
    href = undefined,
  }) => {
    const content = (
      <Card
        elevation={0}
        sx={{
          height: "100%",
          borderRadius: 3,
          border: "1px solid rgba(255,255,255,0.14)",
          background: "rgba(255,255,255,0.06)",
          backdropFilter: "blur(6px)",
          transition: "transform 160ms ease, background 160ms ease",
          "&:hover": {
            transform: "translateY(-2px)",
            background: "rgba(255,255,255,0.09)",
          },
        }}
      >
        <CardContent
          sx={{
            p: { xs: 2, md: 2.5 },
            display: "flex",
            flexDirection: "column",
            alignItems: "center",
            textAlign: "center",
            gap: 1,
          }}
        >
          <CustomRoundButton onClick={onClick} aria-label={label}>
            {icon}
          </CustomRoundButton>

          <Typography
            sx={{
              color: appTheme.palette.yellow.main,
              fontSize: { xs: 16, md: 18, lg: 20 },
              textTransform: "uppercase",
              letterSpacing: 1.2,
              fontFamily: headingFont,
            }}
          >
            {label}
          </Typography>

          <Typography
            sx={{
              color: appTheme.palette.white.main,
              fontSize: { xs: 13, md: 14 },
              fontFamily: bodyFont,
              opacity: 0.9,
            }}
          >
            {title}
          </Typography>

          <Stack spacing={0.25} sx={{ mt: 0.5 }}>
            {lines.map((t) => (
              <Typography
                key={t}
                sx={{
                  color: appTheme.palette.white.main,
                  fontSize: { xs: 12, md: 13 },
                  fontFamily: bodyFont,
                  opacity: 0.95,
                }}
              >
                {t}
              </Typography>
            ))}
          </Stack>
        </CardContent>
      </Card>
    );

    if (href) {
      return (
        <Box
          component="a"
          href={href}
          sx={{ textDecoration: "none", display: "block", height: "100%" }}
        >
          {content}
        </Box>
      );
    }

    return content;
  };

  return (
    <Box
      id="contact"
      sx={{ width: "100%", background: "transparent", position: "relative" }}
    >
      <Box
        sx={{
          color: appTheme.palette.white.main,
          pt: { xs: 10, md: 14, lg: 12 },
          pb: { xs: 8, md: 9 },
          position: "relative",
        }}
      >
        <Container maxWidth="lg" sx={{ position: "relative" }}>
          <Stack
            spacing={1}
            sx={{ alignItems: { xs: "flex-start", md: "center" } }}
          >
            <Typography
              variant={isSmDown ? "h4" : "h2"}
              sx={{
                fontFamily: headingFont,
                textTransform: "uppercase",
                letterSpacing: { xs: 1, md: 4 },
              }}
            >
              Get in touch
            </Typography>

            <Typography
              sx={{
                fontFamily: bodyFont,
                opacity: 0.85,
                maxWidth: 760,
                textAlign: { xs: "left", md: "center" },
                fontSize: { xs: 14, md: 16 },
              }}
            >
              Questions, partnerships, or bookings support — send us a message
              and we’ll get back to you.
            </Typography>
          </Stack>

          <Grid container spacing={2} sx={{ mt: { xs: 4, md: 5 } }}>
            <Grid item xs={12} md={4}>
              <ContactTile
                label="Come say Hello"
                title="Headquarters"
                lines={["Level 10, 55 Market Street", "Sydney, NSW 2000"]}
                onClick={openMaps}
                icon={
                  <PlaceIcon
                    sx={{
                      color: appTheme.palette.green.main,
                      fontSize: { xs: 36, md: 44 },
                    }}
                  />
                }
              />
            </Grid>

            <Grid item xs={12} md={4}>
              <ContactTile
                label="Let's Talk"
                title="Direct support"
                lines={["0476 823 797"]}
                href="tel:+61476823797"
                icon={
                  <LocalPhoneIcon
                    sx={{
                      color: appTheme.palette.green.main,
                      fontSize: { xs: 36, md: 44 },
                    }}
                  />
                }
              />
            </Grid>

            <Grid item xs={12} md={4}>
              <ContactTile
                label="Drop us a note"
                title="Anytime, any topic"
                lines={["info@7-day.com"]}
                href="mailto:info@7-day.com"
                icon={
                  <MailOutlineIcon
                    sx={{
                      color: appTheme.palette.green.main,
                      fontSize: { xs: 36, md: 44 },
                    }}
                  />
                }
              />
            </Grid>
          </Grid>
        </Container>
      </Box>

      <Container maxWidth="lg" sx={{ py: { xs: 4, md: 7 } }}>
        <Grid container spacing={3} alignItems="stretch">
          <Grid item xs={12} md={6}>
            <Card elevation={0} sx={glassCardSx}>
              <CardContent sx={{ p: { xs: 2.5, md: 3.5 } }}>
                <Typography
                  sx={{
                    color: appTheme.palette.white.main,
                    fontWeight: 900,
                    fontSize: { xs: 22, md: 28 },
                    fontFamily: headingFont,
                  }}
                >
                  Message Us
                </Typography>

                <Typography
                  sx={{
                    color: "rgba(255,255,255,0.9)",
                    mt: 1.5,
                    fontFamily: bodyFont,
                    fontSize: { xs: 14, md: 16 },
                    lineHeight: 1.7,
                  }}
                >
                  Whether you’re a host, guest, contractor, or just curious —
                  we’re happy to help. Send us a message and we’ll get back to
                  you shortly.
                </Typography>

                <Typography
                  sx={{
                    color: "rgba(255,255,255,0.9)",
                    mt: 1.5,
                    fontFamily: bodyFont,
                    fontSize: { xs: 14, md: 16 },
                    lineHeight: 1.7,
                  }}
                >
                  For employment opportunities, please apply via{" "}
                  <Link
                    style={{
                      color: appTheme.palette.white.main,
                      textDecoration: "underline",
                      fontWeight: 700,
                    }}
                    target="_blank"
                    to={"https://www.seek.com.au/jobs?advertiserid=61669642"}
                  >
                    SEEK
                  </Link>{" "}
                  and our team will be in touch if your application is
                  shortlisted.
                </Typography>

                <Divider
                  sx={{
                    my: { xs: 2.5, md: 3 },
                    borderColor: "rgba(255,255,255,0.16)",
                  }}
                />

                <Stack spacing={1}>
                  <Typography
                    sx={{
                      color: "rgba(255,255,255,0.88)",
                      fontFamily: bodyFont,
                      fontSize: { xs: 13, md: 14 },
                    }}
                  >
                    Prefer email?{" "}
                    <Box
                      component="a"
                      href="mailto:info@7-day.com"
                      sx={{
                        color: appTheme.palette.white.main,
                        textDecoration: "none",
                        fontWeight: 900,
                      }}
                    >
                      info@7-day.com
                    </Box>
                  </Typography>

                  <Typography
                    sx={{
                      color: "rgba(255,255,255,0.88)",
                      fontFamily: bodyFont,
                      fontSize: { xs: 13, md: 14 },
                    }}
                  >
                    Or call{" "}
                    <Box
                      component="a"
                      href="tel:+61476823797"
                      sx={{
                        color: appTheme.palette.white.main,
                        textDecoration: "none",
                        fontWeight: 900,
                      }}
                    >
                      0476 823 797
                    </Box>
                  </Typography>
                </Stack>
              </CardContent>
            </Card>
          </Grid>

          <Grid item xs={12} md={6}>
            <Card elevation={0} sx={glassCardSx}>
              <CardContent sx={{ p: { xs: 2.5, md: 3.5 } }}>
                <Typography
                  sx={{
                    color: appTheme.palette.white.main,
                    fontWeight: 900,
                    fontSize: { xs: 18, md: 20 },
                    fontFamily: headingFont,
                    mb: 2,
                  }}
                >
                  Send a message
                </Typography>

                <Box component="form" onSubmit={handleSubmit}>
                  <Grid container spacing={2}>
                    <Grid item xs={12} sm={6}>
                      <TextField
                        placeholder="First Name"
                        name="user_first"
                        fullWidth
                        value={values.firstName}
                        onChange={(e) =>
                          setValues((v) => ({
                            ...v,
                            firstName: e.target.value,
                          }))
                        }
                        sx={fieldSx}
                      />
                    </Grid>

                    <Grid item xs={12} sm={6}>
                      <TextField
                        placeholder="Last Name"
                        name="user_last"
                        fullWidth
                        value={values.lastName}
                        onChange={(e) =>
                          setValues((v) => ({ ...v, lastName: e.target.value }))
                        }
                        sx={fieldSx}
                      />
                    </Grid>

                    <Grid item xs={12}>
                      <TextField
                        placeholder="Email"
                        name="user_email"
                        fullWidth
                        value={values.email}
                        onChange={(e) =>
                          setValues((v) => ({ ...v, email: e.target.value }))
                        }
                        onBlur={() => setTouched((t) => ({ ...t, email: true }))}
                        error={emailInvalid}
                        helperText={emailInvalid ? "Please enter a valid email address" : ""}
                        sx={fieldSx}
                      />
                      <Typography
                        sx={{
                          mt: 0.75,
                          fontFamily: bodyFont,
                          fontSize: 12,
                          color: "rgba(255,255,255,0.75)",
                        }}
                      >
                        We’ll only use this to reply.
                      </Typography>
                    </Grid>

                    <Grid item xs={12}>
                      <TextField
                        placeholder="Message"
                        name="user_message"
                        fullWidth
                        value={values.comment}
                        onChange={(e) =>
                          setValues((v) => ({ ...v, comment: e.target.value }))
                        }
                        multiline
                        rows={4}
                        inputProps={{ maxLength: 150 }}
                        sx={fieldSx}
                      />
                      <Typography
                        sx={{
                          mt: 0.75,
                          fontFamily: bodyFont,
                          fontSize: 12,
                          color: "rgba(255,255,255,0.75)",
                        }}
                      >
                        {values.comment.length}/150
                      </Typography>
                    </Grid>

                    <Grid item xs={12}>
                      <Button
                        fullWidth
                        sx={{
                          px: 2.8,
                          py: 1.25,
                          borderRadius: 3,
                          backgroundColor: appTheme.palette.yellow.main,
                          color: appTheme.palette.white.main,
                          fontWeight: 900,
                          letterSpacing: ".10rem",
                          textTransform: "uppercase",
                          "&:hover": {
                            backgroundColor: darken(appTheme.palette.yellow.main, 0.12),
                            color: appTheme.palette.white.main,
                          },
                          "&.Mui-disabled": {
                            backgroundColor: darken(appTheme.palette.yellow.main, 0.25),
                            color: "rgba(255,255,255,0.75)",
                            opacity: 1,
                          },
                        }}
                        disabled={validationCheck}
                        type="submit"
                        variant="contained"
                      >
                        Submit
                      </Button>
                    </Grid>
                  </Grid>
                </Box>
              </CardContent>
            </Card>
          </Grid>
        </Grid>
      </Container>
    </Box>
  );
};